import React from 'react';
import { HelpTooltip } from '../help/HelpTooltip';
import type { HelpTooltipContent } from '../help/HelpTooltip';

interface StatCardWithGrowthProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  subtitle?: string;
  gradientColor?: string;
  isUpdating?: boolean;
  // YoY Growth indicators
  growthPercentage?: number | null;
  growthLabel?: string;  // e.g. "vs prior 2Y" — defaults to "vs last year"
  showGrowth?: boolean;
  dataQualityScore?: number;
  lastValidated?: Date | null;
  validationConfidence?: 'HIGH' | 'MEDIUM' | 'LOW' | null;
  /** Optional inline help next to the title (e.g. why a figure is unavailable). */
  helpTooltip?: HelpTooltipContent;
}

const StatCardWithGrowth: React.FC<StatCardWithGrowthProps> = ({
  title,
  value,
  icon,
  subtitle,
  gradientColor = '#3b82f6',
  isUpdating = false,
  growthPercentage = null,
  growthLabel = 'vs last year',
  showGrowth = true,
  dataQualityScore,
  lastValidated,
  validationConfidence,
  helpTooltip,
}) => {
    // Helper function to convert hex to RGB
    const hexToRgb = (hex: string) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : null;
    };

    // Create a stronger gradient effect
    const getGradientStyle = () => {
        return {
            background: `linear-gradient(135deg, rgba(31, 41, 55, 0.8) 0%, rgba(31, 41, 55, 0.95) 50%, rgba(17, 24, 39, 1) 100%)`,
        };
    };

    const getColoredGradientStyle = () => {
        const rgb = hexToRgb(gradientColor);
        if (rgb) {
            return {
                background: `linear-gradient(135deg,
                    rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15) 0%,
                    rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.05) 50%,
                    rgba(17, 24, 39, 0.8) 100%)`
            };
        }
        return {};
    };

    // Data quality helpers
    const getQualityBadgeColor = () => {
        if (!dataQualityScore) return null;
        if (dataQualityScore >= 99) return { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30', label: 'Verified' };
        if (dataQualityScore >= 95) return { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30', label: 'Good' };
        return { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30', label: 'Warning' };
    };

    const getConfidenceBadge = () => {
        if (!validationConfidence) return null;
        const badges: Record<'HIGH' | 'MEDIUM' | 'LOW', { icon: string; color: string; label: string }> = {
            HIGH: { icon: '✓', color: 'text-green-400', label: '100% Confidence' },
            MEDIUM: { icon: '~', color: 'text-yellow-400', label: '~95% Confidence' },
            LOW: { icon: '!', color: 'text-red-400', label: 'Low Confidence' },
        };
        return badges[validationConfidence];
    };

    const formatLastValidated = () => {
        if (!lastValidated) return null;
        const now = new Date();
        const diff = now.getTime() - lastValidated.getTime();
        const minutes = Math.floor(diff / 60000);
        if (minutes < 1) return 'Just now';
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours}h ago`;
        const days = Math.floor(hours / 24);
        return `${days}d ago`;
    };

    // Growth indicator helpers
    const getGrowthColor = () => {
        if (growthPercentage === null || growthPercentage === undefined) return null;
        if (growthPercentage > 0) return 'text-green-400';
        if (growthPercentage < 0) return 'text-red-400';
        return 'text-gray-400';
    };

    const getGrowthIcon = () => {
        if (growthPercentage === null || growthPercentage === undefined) return '→';
        if (growthPercentage > 0) return '↑';
        if (growthPercentage < 0) return '↓';
        return '→';
    };

    const formatGrowthPercentage = () => {
        if (growthPercentage === null || growthPercentage === undefined) {
            return 'No prior year data';
        }
        const absValue = Math.abs(growthPercentage);
        // THE display ceiling for every "vs last year" badge in the app.
        // calculateGrowth deliberately does NOT clamp: a huge-but-finite growth is
        // real, and returning null there would render "No prior year data" when
        // prior-year data exists. Capping it here is truthful and keeps one rule.
        if (absValue > 999) return '>999%';
        return `${absValue.toFixed(1)}%`;
    };

    const qualityBadge = getQualityBadgeColor();
    const confidenceBadge = getConfidenceBadge();
    const growthColor = getGrowthColor();
    const growthIcon = getGrowthIcon();

    return (
        <div className="relative rounded-lg shadow-xl overflow-hidden group hover:shadow-2xl transition-all duration-300">
            {/* Base dark gradient background */}
            <div
                className="absolute inset-0"
                style={getGradientStyle()}
            />

            {/* Colored gradient overlay */}
            <div
                className="absolute inset-0 opacity-60"
                style={getColoredGradientStyle()}
            />

            {/* Top accent gradient line */}
            <div
                className="absolute top-0 left-0 right-0 h-1"
                style={{
                    background: `linear-gradient(90deg,
                        transparent 0%,
                        ${gradientColor} 15%,
                        ${gradientColor} 85%,
                        transparent 100%)`
                }}
            />

            {/* Content */}
            <div className="relative p-6 z-10">
                <div className="flex justify-between items-start">
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <p className="text-gray-400 text-sm font-medium uppercase tracking-wider">
                                {title}
                            </p>
                            {helpTooltip && (
                                <HelpTooltip content={helpTooltip} ariaLabel={`About ${title}`} iconSize={12} />
                            )}
                            {/* Updating badge */}
                            {isUpdating && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30 animate-pulse">
                                    Updating...
                                </span>
                            )}
                            {/* Quality badge */}
                            {qualityBadge && (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${qualityBadge.bg} ${qualityBadge.text} border ${qualityBadge.border}`}>
                                    {qualityBadge.label}
                                </span>
                            )}
                            {/* Confidence badge */}
                            {confidenceBadge && (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${confidenceBadge.color}`} title="Data validation confidence">
                                    {confidenceBadge.icon} {confidenceBadge.label}
                                </span>
                            )}
                        </div>
                        <p className={`text-4xl font-bold text-white transition-opacity duration-300 ${isUpdating ? 'opacity-75' : 'opacity-100'}`}>
                            <span data-ph-mask>{value}</span>
                        </p>

                        {/* YoY Growth Indicator */}
                        {showGrowth && (
                            <div className="mt-2">
                                <div className="flex items-center gap-1.5">
                                    <span className={`text-lg font-semibold ${growthColor || 'text-gray-400'}`}>
                                        {growthIcon}
                                    </span>
                                    <span className={`text-sm font-medium ${growthColor || 'text-gray-400'}`}>
                                        {formatGrowthPercentage()}
                                    </span>
                                    <span className="text-xs text-gray-500">{growthLabel}</span>
                                </div>
                            </div>
                        )}

                        <div className="flex items-center gap-2 mt-3">
                            <p className="text-xs text-gray-500">
                                For selected period
                                {subtitle && <span className="ml-1">• {subtitle}</span>}
                            </p>
                            {/* Last validated timestamp */}
                            {lastValidated && (
                                <span className="text-xs text-gray-600" title={`Last validated: ${lastValidated.toLocaleString()}`}>
                                    • Validated {formatLastValidated()}
                                </span>
                            )}
                        </div>
                    </div>
                    {icon && (
                        <div
                            className="ml-4 p-3 rounded-xl"
                            style={{
                                background: `linear-gradient(135deg,
                                    ${gradientColor}30 0%,
                                    ${gradientColor}10 100%)`,
                                boxShadow: `0 0 20px ${gradientColor}20`
                            }}
                        >
                            <div style={{ color: gradientColor }}>
                                {icon}
                            </div>
                        </div>
                    )}
                </div>

                {/* Bottom gradient glow */}
                <div
                    className="absolute bottom-0 left-0 right-0 h-20 -z-10 opacity-20 blur-xl"
                    style={{
                        background: `radial-gradient(ellipse at center bottom, ${gradientColor}, transparent)`
                    }}
                />
            </div>
        </div>
    );
};

export default StatCardWithGrowth;
